import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    ArrowDown,
    ArrowUp,
    Clapperboard,
    Copy,
    Film,
    Loader2,
    Music,
    RefreshCw,
    Search,
    Sparkles,
    Trash2,
    Upload,
    X,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select';
import { SegmentedChoice } from '@/components/design-system/common';
import { CsRecipe } from '../../../../common/models/content-studio.model';
import type { PlannerProduct } from '../planner/plan-compiler';
import { resolveCreditCost } from '../recipe-pricing';
import { ASPECT_RATIOS, AspectRatio } from './composition';
import { TEXT_PRESETS } from './presets';
import {
    Storyboard,
    StoryboardRequest,
    StorySceneSpec,
    nextSceneId,
    storyboardSeconds,
    videoInputsFor,
} from './video-story';
import { PanelSection } from './controls';

const LENGTHS = [10, 15, 20, 30] as const;
const MAX_PRODUCTS = 6;

interface StoryboardDialogProps {
    open: boolean;
    onClose: () => void;
    products: PlannerProduct[];
    initialProductUuids?: string[];
    recipes: CsRecipe[];
    /** Writes the storyboard (POST /storyboard). */
    onWrite: (req: StoryboardRequest) => Promise<Storyboard>;
    /** Lays it out on the canvas and starts shooting. */
    onCreate: (sb: Storyboard, req: StoryboardRequest) => Promise<void>;
    /** Uploads one inspiration frame, resolving to its URL. */
    uploadFrame: (file: File) => Promise<string>;
}

interface Frame {
    preview: string;
    url?: string;
}

/** Evenly spaced frames of a local video, as JPEG files. */
async function extractFrames(file: File, count: number): Promise<Blob[]> {
    const src = URL.createObjectURL(file);
    try {
        const el = document.createElement('video');
        el.muted = true;
        el.preload = 'auto';
        el.playsInline = true;
        el.src = src;
        await new Promise<void>((resolve, reject) => {
            el.onloadeddata = () => resolve();
            el.onerror = () => reject(new Error('unreadable video'));
        });
        const duration = el.duration || 1;
        const scale = Math.min(1, 768 / Math.max(el.videoWidth, el.videoHeight));
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(el.videoWidth * scale);
        canvas.height = Math.round(el.videoHeight * scale);
        const ctx = canvas.getContext('2d')!;
        const out: Blob[] = [];
        for (let i = 0; i < count; i++) {
            el.currentTime = (duration * (i + 0.5)) / count;
            await new Promise<void>((resolve) => {
                const done = () => resolve();
                el.addEventListener('seeked', done, { once: true });
                setTimeout(done, 4000);
            });
            ctx.drawImage(el, 0, 0, canvas.width, canvas.height);
            const blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, 'image/jpeg', 0.85));
            if (blob) out.push(blob);
        }
        return out;
    } finally {
        URL.revokeObjectURL(src);
    }
}

export const StoryboardDialog: React.FC<StoryboardDialogProps> = ({
    open,
    onClose,
    products,
    initialProductUuids,
    recipes,
    onWrite,
    onCreate,
    uploadFrame,
}) => {
    const { t, i18n } = useTranslation();
    const [step, setStep] = useState<'brief' | 'writing' | 'board' | 'creating'>('brief');
    const [brief, setBrief] = useState('');
    const [selected, setSelected] = useState<string[]>([]);
    const [search, setSearch] = useState('');
    const [aspect, setAspect] = useState<AspectRatio>('9:16');
    const [length, setLength] = useState<number>(15);
    const [frames, setFrames] = useState<Frame[]>([]);
    const [extracting, setExtracting] = useState(false);
    const [board, setBoard] = useState<Storyboard | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [elapsed, setElapsed] = useState(0);
    const fileRef = useRef<HTMLInputElement>(null);

    // A fresh form each time it opens, seeded with whatever products were selected.
    useEffect(() => {
        if (!open) return;
        setStep('brief');
        setBoard(null);
        setError(null);
        setSelected((initialProductUuids || []).slice(0, MAX_PRODUCTS));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    useEffect(() => {
        if (step !== 'writing' && step !== 'creating') return undefined;
        setElapsed(0);
        const t0 = Date.now();
        const id = window.setInterval(() => setElapsed(Math.round((Date.now() - t0) / 1000)), 1000);
        return () => window.clearInterval(id);
    }, [step]);

    const productByUuid = useMemo(() => new Map(products.map((p) => [p.uuid, p])), [products]);
    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return products
            .filter((p) => !q || p.name?.toLowerCase().includes(q))
            .slice(0, 48);
    }, [products, search]);

    const request = (): StoryboardRequest => ({
        brief: brief.trim(),
        productUuids: selected,
        aspect,
        targetSec: length,
        inspirationFrames: frames.map((f) => f.url).filter(Boolean) as string[],
        language: i18n.language?.slice(0, 2),
    });

    const write = async () => {
        setError(null);
        setStep('writing');
        try {
            const sb = await onWrite(request());
            setBoard({ ...sb, aspect: sb.aspect || aspect });
            setStep('board');
        } catch (err: any) {
            setError(t('VIDEO_EDITOR.STORY.WRITE_FAILED'));
            // eslint-disable-next-line no-console
            console.error('[video-story] storyboard failed', err);
            setStep(board ? 'board' : 'brief');
        }
    };

    const create = async () => {
        if (!board) return;
        setStep('creating');
        setError(null);
        try {
            await onCreate(board, request());
            onClose();
        } catch (err: any) {
            setError(err?.message || t('VIDEO_EDITOR.STORY.CREATE_FAILED'));
            setStep('board');
        }
    };

    const addFramesFrom = async (file?: File | null) => {
        if (!file) return;
        setExtracting(true);
        setError(null);
        try {
            const blobs = await extractFrames(file, 8);
            const local = blobs.map((b) => ({ preview: URL.createObjectURL(b) }));
            setFrames(local);
            const urls = await Promise.all(blobs.map((b, i) => uploadFrame(new File([b], `inspiration-${i + 1}.jpg`, { type: 'image/jpeg' }))
                .catch(() => undefined)));
            setFrames(local.map((f, i) => ({ ...f, url: urls[i] })));
        } catch {
            setError(t('VIDEO_EDITOR.STORY.FRAMES_FAILED'));
        } finally {
            setExtracting(false);
        }
    };

    const toggleProduct = (uuid: string) => {
        setSelected((prev) => (prev.includes(uuid)
            ? prev.filter((u) => u !== uuid)
            : prev.length >= MAX_PRODUCTS ? prev : [...prev, uuid]));
    };

    // ─── board editing ──────────────────────────────────────────────

    const patchScene = (id: string, patch: Partial<StorySceneSpec>) => {
        setBoard((b) => (b ? { ...b, scenes: b.scenes.map((s) => (s.id === id ? { ...s, ...patch } : s)) } : b));
    };
    const moveScene = (index: number, by: number) => {
        setBoard((b) => {
            if (!b) return b;
            const scenes = b.scenes.slice();
            const to = index + by;
            if (to < 0 || to >= scenes.length) return b;
            [scenes[index], scenes[to]] = [scenes[to], scenes[index]];
            return { ...b, scenes };
        });
    };
    const removeScene = (id: string) => setBoard((b) => (b ? { ...b, scenes: b.scenes.filter((s) => s.id !== id) } : b));
    const duplicateScene = (index: number) => {
        setBoard((b) => {
            if (!b) return b;
            const copy: StorySceneSpec = { ...b.scenes[index], id: nextSceneId(b), caption: undefined };
            const scenes = b.scenes.slice();
            scenes.splice(index + 1, 0, copy);
            return { ...b, scenes };
        });
    };

    const i2v = recipes.find((r) => r.slug === 'image-to-video');
    const cameraMoves = (i2v?.inputs || []).find((i) => i.key === 'cameraMove')?.options || [];
    const credits = useMemo(() => {
        if (!board) return 0;
        return board.scenes.reduce((sum, s) => {
            const recipe = recipes.find((r) => r.slug === s.recipeSlug);
            const product = productByUuid.get(s.productUuid);
            const still = recipe ? resolveCreditCost(recipe, { inputs: s.imageInputs, visualizationType: product?.visualizationType as any }) : 0;
            const clip = i2v ? resolveCreditCost(i2v, { inputs: videoInputsFor(s, board.aspect) }) : 0;
            return sum + still + clip;
        }, 0);
    }, [board, recipes, i2v, productByUuid]);

    const canWrite = (!!brief.trim() || selected.length > 0) && !extracting;
    const busy = step === 'writing' || step === 'creating';

    return (
        <Dialog open={open} onOpenChange={(next) => { if (!next && !busy) onClose(); }}>
            <DialogContent
                showCloseButton={false}
                onOpenAutoFocus={(e) => e.preventDefault()}
                className="flex h-[88vh] w-[96vw] max-w-[980px] flex-col gap-0 overflow-hidden bg-background p-0 sm:max-w-[980px]"
                data-vs-dialog={step}
            >
                <div className="flex h-12 shrink-0 items-center gap-2 border-b px-5">
                    <Clapperboard className="size-4 text-muted-foreground" />
                    <DialogTitle className="font-heading text-sm font-medium tracking-tight text-foreground">
                        {t('VIDEO_EDITOR.STORY.TITLE')}
                    </DialogTitle>
                    <span className="text-xs text-muted-foreground">
                        {step === 'board' || step === 'creating'
                            ? t('VIDEO_EDITOR.STORY.STEP_BOARD')
                            : t('VIDEO_EDITOR.STORY.STEP_BRIEF')}
                    </span>
                    <Button type="button" variant="ghost" size="icon" className="ms-auto" onClick={onClose} disabled={busy} aria-label={t('VIDEO_EDITOR.CLOSE')}>
                        <X />
                    </Button>
                </div>

                <div className="min-h-0 flex-1 overflow-y-auto">
                    {step === 'brief' && (
                        <div className="grid grid-cols-1 md:grid-cols-[1fr_360px]">
                            <div className="border-e">
                                <PanelSection title={t('VIDEO_EDITOR.STORY.BRIEF')}>
                                    <Textarea
                                        value={brief}
                                        onChange={(e) => setBrief(e.target.value)}
                                        placeholder={t('VIDEO_EDITOR.STORY.BRIEF_PLACEHOLDER')}
                                        className="min-h-[120px] text-sm"
                                        data-vs-brief
                                    />
                                </PanelSection>
                                <PanelSection title={t('VIDEO_EDITOR.STORY.FORMAT')}>
                                    <SegmentedChoice<AspectRatio>
                                        value={aspect}
                                        options={ASPECT_RATIOS.map((a) => ({ value: a, label: a }))}
                                        onChange={setAspect}
                                    />
                                    <span className="text-xs text-muted-foreground">{t('VIDEO_EDITOR.STORY.LENGTH')}</span>
                                    <SegmentedChoice<number>
                                        value={length}
                                        options={LENGTHS.map((l) => ({ value: l, label: `${l} s` }))}
                                        onChange={setLength}
                                    />
                                </PanelSection>
                                <PanelSection
                                    title={t('VIDEO_EDITOR.STORY.INSPIRATION')}
                                    action={frames.length > 0 && !extracting ? (
                                        <Button type="button" size="xs" variant="ghost" onClick={() => setFrames([])}>
                                            {t('VIDEO_EDITOR.STORY.REMOVE')}
                                        </Button>
                                    ) : undefined}
                                >
                                    <input
                                        ref={fileRef}
                                        type="file"
                                        accept="video/*"
                                        className="hidden"
                                        onChange={(e) => { void addFramesFrom(e.target.files?.[0]); e.target.value = ''; }}
                                    />
                                    {frames.length === 0 ? (
                                        <button
                                            type="button"
                                            onClick={() => fileRef.current?.click()}
                                            onDragOver={(e) => e.preventDefault()}
                                            onDrop={(e) => { e.preventDefault(); void addFramesFrom(e.dataTransfer.files?.[0]); }}
                                            className="flex flex-col items-center gap-1.5 rounded-lg border border-dashed px-4 py-6 text-center text-xs text-muted-foreground transition-colors hover:border-foreground/40 hover:bg-muted/50"
                                        >
                                            {extracting ? <Loader2 className="size-4 animate-spin" /> : <Upload className="size-4" />}
                                            <span className="text-sm text-foreground">{t('VIDEO_EDITOR.STORY.INSPIRATION_DROP')}</span>
                                            {t('VIDEO_EDITOR.STORY.INSPIRATION_HINT')}
                                        </button>
                                    ) : (
                                        <div className="flex gap-1.5 overflow-x-auto pb-1" data-vs-frames>
                                            {frames.map((f, i) => (
                                                <div key={i} className="relative h-24 w-16 shrink-0 overflow-hidden rounded-md border bg-muted">
                                                    <img src={f.preview} alt="" className="size-full object-cover" />
                                                    {!f.url && (
                                                        <span className="absolute inset-0 flex items-center justify-center bg-background/50">
                                                            <Loader2 className="size-3.5 animate-spin text-muted-foreground" />
                                                        </span>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </PanelSection>
                            </div>
                            <div className="flex min-h-0 flex-col">
                                <PanelSection title={t('VIDEO_EDITOR.STORY.PRODUCTS', { count: selected.length })}>
                                    {selected.length > 0 && (
                                        <div className="flex flex-wrap gap-1.5">
                                            {selected.map((u) => (
                                                <button
                                                    key={u}
                                                    type="button"
                                                    onClick={() => toggleProduct(u)}
                                                    className="flex items-center gap-1.5 rounded-full border bg-muted/60 py-0.5 pe-2 ps-0.5 text-xs text-foreground hover:bg-muted"
                                                >
                                                    <ProductThumb product={productByUuid.get(u)} className="size-5 rounded-full" />
                                                    <span className="max-w-[140px] truncate">{productByUuid.get(u)?.name || u.slice(0, 8)}</span>
                                                    <X className="size-3 text-muted-foreground" />
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                    <div className="flex h-8 items-center gap-2 rounded-lg border px-2.5 focus-within:border-ring">
                                        <Search className="size-3.5 text-muted-foreground" />
                                        <input
                                            value={search}
                                            onChange={(e) => setSearch(e.target.value)}
                                            placeholder={t('VIDEO_EDITOR.STORY.SEARCH_PRODUCTS')}
                                            className="w-full bg-transparent text-sm outline-none"
                                        />
                                    </div>
                                    <div className="grid max-h-[360px] auto-rows-max grid-cols-3 gap-1.5 overflow-y-auto pe-0.5">
                                        {filtered.map((p) => {
                                            const on = selected.includes(p.uuid);
                                            return (
                                                <button
                                                    key={p.uuid}
                                                    type="button"
                                                    onClick={() => toggleProduct(p.uuid)}
                                                    aria-pressed={on}
                                                    data-vs-product={p.uuid}
                                                    title={p.name}
                                                    className={cn(
                                                        'flex flex-col overflow-hidden rounded-lg border text-start transition-colors hover:bg-muted/60',
                                                        on && 'border-foreground outline outline-[1.5px] outline-foreground',
                                                    )}
                                                >
                                                    <ProductThumb product={p} className="h-[84px] w-full" />
                                                    <span className="truncate px-1.5 py-1 text-[11px] text-foreground">{p.name}</span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </PanelSection>
                            </div>
                        </div>
                    )}

                    {(step === 'writing' || (step === 'creating' && !board)) && (
                        <div className="flex flex-col gap-3 p-6" data-vs-writing>
                            <div className="flex items-center gap-2 text-sm text-foreground">
                                <Loader2 className="size-4 animate-spin text-muted-foreground" />
                                {t('VIDEO_EDITOR.STORY.WRITING')}
                                <span className="text-xs tabular-nums text-muted-foreground">{elapsed}s</span>
                            </div>
                            <p className="text-xs text-muted-foreground">{t('VIDEO_EDITOR.STORY.WRITING_HINT')}</p>
                            {[0, 1, 2, 3, 4].map((i) => (
                                <div key={i} className="h-[76px] animate-pulse rounded-xl border bg-muted/50" style={{ animationDelay: `${i * 120}ms` }} />
                            ))}
                        </div>
                    )}

                    {(step === 'board' || step === 'creating') && board && (
                        <div className="flex flex-col">
                            <PanelSection title={t('VIDEO_EDITOR.STORY.STORYBOARD')}>
                                <Input
                                    value={board.title}
                                    onChange={(e) => setBoard({ ...board, title: e.target.value })}
                                    className="text-base font-medium"
                                />
                                {board.concept && <p className="text-xs leading-relaxed text-muted-foreground">{board.concept}</p>}
                            </PanelSection>
                            <div className="flex flex-col gap-2 px-4 py-4" data-vs-scenes>
                                {board.scenes.map((s, i) => {
                                    const product = productByUuid.get(s.productUuid);
                                    return (
                                        <div key={s.id} className="flex gap-3 rounded-xl border bg-background p-3" data-vs-scene={s.id}>
                                            <div className="flex w-6 shrink-0 flex-col items-center gap-1 pt-0.5">
                                                <span className="text-xs tabular-nums text-muted-foreground">{i + 1}</span>
                                                <IconBtn label={t('VIDEO_EDITOR.STORY.MOVE_UP')} onClick={() => moveScene(i, -1)} disabled={i === 0}><ArrowUp /></IconBtn>
                                                <IconBtn label={t('VIDEO_EDITOR.STORY.MOVE_DOWN')} onClick={() => moveScene(i, 1)} disabled={i === board.scenes.length - 1}><ArrowDown /></IconBtn>
                                            </div>
                                            <ProductThumb product={product} className="h-[92px] w-[68px] shrink-0 rounded-lg" />
                                            <div className="flex min-w-0 flex-1 flex-col gap-2">
                                                <div className="flex items-center gap-2 text-xs">
                                                    <span className="rounded-md border px-1.5 py-px text-foreground">{t(`VIDEO_EDITOR.STORY.SHOT_${String(s.shot || 'product').toUpperCase()}`)}</span>
                                                    <span className="truncate text-muted-foreground">{product?.name}</span>
                                                </div>
                                                <p className="line-clamp-2 text-xs leading-relaxed text-muted-foreground">{s.description}</p>
                                                <div className="grid grid-cols-[1fr_92px_150px] gap-2">
                                                    <Input
                                                        value={s.caption?.text || ''}
                                                        placeholder={t('VIDEO_EDITOR.STORY.CAPTION_PLACEHOLDER')}
                                                        onChange={(e) => patchScene(s.id, {
                                                            caption: e.target.value
                                                                ? { text: e.target.value, preset: s.caption?.preset || board.captionPreset }
                                                                : undefined,
                                                        })}
                                                        className="h-8 text-sm"
                                                    />
                                                    <div className="flex h-8 items-center rounded-lg border px-2 text-sm focus-within:border-ring">
                                                        <input
                                                            type="number"
                                                            step={0.1}
                                                            min={0.6}
                                                            max={5}
                                                            value={s.durationSec}
                                                            onChange={(e) => {
                                                                const v = parseFloat(e.target.value);
                                                                if (Number.isFinite(v)) patchScene(s.id, { durationSec: Math.max(0.6, Math.min(5, v)) });
                                                            }}
                                                            className="w-full min-w-0 bg-transparent tabular-nums outline-none"
                                                            aria-label={t('VIDEO_EDITOR.INSPECTOR.DURATION')}
                                                        />
                                                        <span className="text-xs text-muted-foreground">s</span>
                                                    </div>
                                                    <NativeSelect
                                                        size="sm"
                                                        className="w-full"
                                                        value={s.motion?.cameraMove || ''}
                                                        onChange={(e) => patchScene(s.id, { motion: { ...(s.motion || { lightingTransition: 'none', intensity: 'subtle' }), cameraMove: e.target.value } as any })}
                                                        aria-label={t('VIDEO_EDITOR.STORY.MOTION')}
                                                    >
                                                        {!cameraMoves.some((o) => o.value === s.motion?.cameraMove) && s.motion?.cameraMove && (
                                                            <NativeSelectOption value={s.motion.cameraMove}>{s.motion.cameraMove}</NativeSelectOption>
                                                        )}
                                                        {cameraMoves.map((o) => <NativeSelectOption key={o.value} value={o.value}>{o.label}</NativeSelectOption>)}
                                                    </NativeSelect>
                                                </div>
                                            </div>
                                            <div className="flex shrink-0 flex-col gap-1">
                                                <IconBtn label={t('VIDEO_EDITOR.STORY.DUPLICATE_SCENE')} onClick={() => duplicateScene(i)}><Copy /></IconBtn>
                                                <IconBtn label={t('VIDEO_EDITOR.STORY.DELETE_SCENE')} onClick={() => removeScene(s.id)} disabled={board.scenes.length <= 1} destructive><Trash2 /></IconBtn>
                                            </div>
                                        </div>
                                    );
                                })}
                                <p className="text-[11px] text-muted-foreground">{t('VIDEO_EDITOR.STORY.ADD_HINT')}</p>
                            </div>
                            <PanelSection title={t('VIDEO_EDITOR.STORY.MUSIC')}>
                                <div className="flex items-start gap-2">
                                    <Music className="mt-2 size-4 shrink-0 text-muted-foreground" />
                                    <Textarea
                                        value={board.music?.prompt || ''}
                                        onChange={(e) => setBoard({ ...board, music: { prompt: e.target.value, durationSec: board.music?.durationSec || Math.ceil(storyboardSeconds(board) + 3) } })}
                                        className="min-h-[64px] text-sm"
                                    />
                                </div>
                                <span className="text-[11px] text-muted-foreground">
                                    {t('VIDEO_EDITOR.STORY.CAPTION_STYLE', {
                                        style: t(TEXT_PRESETS.find((p) => p.id === board.captionPreset)?.nameKey || 'VIDEO_EDITOR.PRESET.EDITORIAL_SERIF'),
                                    })}
                                </span>
                            </PanelSection>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex shrink-0 items-center gap-2 border-t px-5 py-3">
                    {error && <p className="me-auto text-xs text-destructive">{error}</p>}
                    {!error && (step === 'board' || step === 'creating') && board && (
                        <p className="me-auto text-xs tabular-nums text-muted-foreground" data-vs-summary>
                            {t('VIDEO_EDITOR.STORY.SUMMARY', {
                                scenes: board.scenes.length,
                                seconds: storyboardSeconds(board),
                                credits: Math.ceil(credits),
                            })}
                        </p>
                    )}
                    {!error && step === 'brief' && (
                        <p className="me-auto text-xs text-muted-foreground">{t('VIDEO_EDITOR.STORY.BRIEF_HINT')}</p>
                    )}
                    {step === 'brief' && (
                        <>
                            <Button type="button" variant="outline" onClick={onClose}>{t('VIDEO_EDITOR.STORY.CANCEL')}</Button>
                            <Button type="button" onClick={write} disabled={!canWrite} data-vs-write>
                                <Sparkles />
                                {t('VIDEO_EDITOR.STORY.WRITE')}
                            </Button>
                        </>
                    )}
                    {(step === 'board' || step === 'creating') && (
                        <>
                            <Button type="button" variant="ghost" onClick={() => setStep('brief')} disabled={step === 'creating'}>
                                {t('VIDEO_EDITOR.STORY.BACK')}
                            </Button>
                            <Button type="button" variant="outline" onClick={write} disabled={step === 'creating'}>
                                <RefreshCw />
                                {t('VIDEO_EDITOR.STORY.REWRITE')}
                            </Button>
                            <Button type="button" onClick={create} disabled={step === 'creating' || !board?.scenes.length} data-vs-create>
                                {step === 'creating' ? <Loader2 className="animate-spin" /> : <Film />}
                                {t('VIDEO_EDITOR.STORY.CREATE')}
                            </Button>
                        </>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};

const ProductThumb: React.FC<{ product?: PlannerProduct; className?: string }> = ({ product, className }) => {
    const src = product?.thumbnailPath || product?.galleryImages?.[0] || product?.originalImagePath || product?.surfaceTexturePath;
    return (
        <span
            className={cn('block shrink-0 bg-muted bg-cover bg-center', className)}
            style={{ backgroundImage: src ? `url(${src})` : undefined }}
        />
    );
};

const IconBtn: React.FC<{ label: string; onClick: () => void; disabled?: boolean; destructive?: boolean; children: React.ReactNode }> = ({
    label,
    onClick,
    disabled,
    destructive,
    children,
}) => (
    <button
        type="button"
        title={label}
        aria-label={label}
        onClick={onClick}
        disabled={disabled}
        className={cn(
            'flex size-6 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-30 [&_svg]:size-3.5',
            destructive && 'hover:text-destructive',
        )}
    >
        {children}
    </button>
);
